import React, { useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getUserSubscriptionFromFirestore } from '../services/firestoreService';
import toast from 'react-hot-toast';
import './Redeem.css';

interface PromoCode {
  id: string;
  status: 'active' | 'redeemed';
  credits: number;
  redeemedAt?: number;
  redeemedBy?: string;
  createdAt: number;
}

const Redeem: React.FC = () => {
  const { userId } = useAuth();
  const { user } = useUser();
  const [promoCode, setPromoCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRedeemPromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userId || !user) {
      toast.error('Please sign in to redeem a promocode');
      return;
    }

    if (!promoCode.trim()) {
      toast.error('Please enter a promocode');
      return;
    }

    setLoading(true);

    try {
      const promoCodeTrimmed = promoCode.trim();
      
      // Get the promocode document
      const promoCodeRef = doc(db, 'promocodes', promoCodeTrimmed);
      const promoCodeSnap = await getDoc(promoCodeRef);
      
      if (!promoCodeSnap.exists()) {
        toast.error('Invalid promocode');
        return;
      }
      
      const promoCodeData = promoCodeSnap.data() as PromoCode;
      
      // Check if promocode is active
      if (promoCodeData.status !== 'active') {
        toast.error('This promocode has already been redeemed');
        return;
      }
      
      // Get current user subscription
      const currentSubscription = await getUserSubscriptionFromFirestore(userId);
      if (!currentSubscription) {
        toast.error('User subscription not found');
        return;
      }
      
      // Check if user is already a lifetime user
      if (currentSubscription.lifetime) {
        toast.error('You are already a lifetime user');
        return;
      }
      
      const userEmail = user.primaryEmailAddress?.emailAddress || user.emailAddresses[0]?.emailAddress;
      if (!userEmail) {
        toast.error('User email not found');
        return;
      }
      
      const creditsToAdd = promoCodeData.credits || 1000;
      
      // Calculate new credits based on current plan
      let newCredits: number;
      if (currentSubscription.plan === 'free_user') {
        // For free users, set credits to the promocode amount (usually 1000)
        newCredits = creditsToAdd;
      } else {
        // For paid users, add to existing credits
        newCredits = currentSubscription.credits + creditsToAdd;
      }
      
      // Update promocode status to redeemed
      await updateDoc(promoCodeRef, {
        status: 'redeemed',
        redeemedAt: serverTimestamp(),
        redeemedBy: userId,
        redeemedByEmail: userEmail,
      });
      
      // Update user subscription - add credits and lifetime flag
      const subscriptionRef = doc(db, 'subscriptions', userId);
      await updateDoc(subscriptionRef, {
        credits: newCredits,
        maxCredits: newCredits,
        lifetime: true,
        updatedAt: serverTimestamp(),
      });
      
      // Add user to lifetimers collection
      const lifetimersRef = doc(db, 'lifetimers', userId);
      await setDoc(lifetimersRef, {
        userId: userId,
        userEmail: userEmail,
        promoCode: promoCodeTrimmed,
        creditsReceived: creditsToAdd,
        redeemedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      
      toast.success(`🎉 Congratulations! You're now a lifetime user with ${creditsToAdd.toLocaleString()} bonus credits!`);
      setPromoCode('');
      
      // Reload subscription to update credits display
      try {
        await getUserSubscriptionFromFirestore(userId);
      } catch (error) {
        console.error('Error reloading subscription:', error);
      }
      
      // Navigate to home after a delay
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
      
    } catch (error) {
      console.error('Error redeeming promocode:', error);
      toast.error('Failed to redeem promocode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="redeem-container">
      <div className="redeem-card">
        <div className="redeem-header">
          <h1>Redeem Lifetime Deal</h1>
          <p>Enter your promocode to unlock lifetime access with bonus credits</p>
        </div>
        
        <form onSubmit={handleRedeemPromoCode} className="redeem-form">
          <div className="input-group">
            <label htmlFor="promocode">Promocode</label>
            <input
              id="promocode"
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="Enter your promocode"
              disabled={loading}
              className="promocode-input"
              maxLength={50}
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading || !promoCode.trim()}
            className="redeem-button"
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Redeeming...
              </>
            ) : (
              'Redeem Promocode'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Redeem; 